import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import DashboardOnboarding from '../dashboard/onboarding/Onboarding';
import GuidedOnboarding from '../dashboard/onboarding/guided/GuidedOnboarding';
import LoadingScreen from '../components/LoadingScreen';
import { Button, useTheme } from '@mui/material';
import { ChevronLeft } from '@mui/icons-material';
import { useWhitelabel } from '../context/WhitelabelContext';
import { useAuth } from '../context/AuthContext';

interface OnboardingProps {
  mode: string;
}

export default function Onboarding({ mode }: OnboardingProps) {
  const [loading, setLoading] = useState(true);
  const { whitelabelSettings, loaded: whitelabelLoaded } = useWhitelabel();
  const { currentUser, signOut, getToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  // When onboarding is opened from the companies page ("Add Company"), Exit
  // should return the user to their companies rather than logging them out.
  const fromCompanies = (location.state as { fromCompanies?: boolean } | null)?.fromCompanies ?? false;

  // Set when the user clicked "Resume Onboarding" in the dashboard menu: the guided flow
  // rehydrates this company and resumes where they left off instead of starting fresh.
  const resumeCompanyId = (location.state as { resumeCompanyId?: string } | null)?.resumeCompanyId;

  useEffect(() => {
    if (currentUser) {
      setLoading(false);
    } else {
      // Not authenticated, redirect to login
      navigate('/login');
    }
  }, [currentUser, navigate]);

  const handleComplete = (companyId: string) => {
    const employeesPath = `/dashboard/companies/${companyId}/employees`;

    // On the main platform we show the subscription page at the END of onboarding (it used to
    // be at the beginning, before the auth flows redirected here). Pass the post-subscribe
    // destination through router state so Subscribe lands the user on the new company's
    // employees page. Whitelabels keep going straight into the app.
    if (whitelabelSettings?.isMainSite) {
      console.log('[Onboarding] Onboarding complete - showing subscription page for company:', companyId);
      navigate('/subscribe', { state: { nextPath: employeesPath } });
      return;
    }

    // After onboarding, go to the employees page of the new company
    console.log('[Onboarding] Onboarding complete - going to employees page for company:', companyId);
    navigate(employeesPath);
  };

  const handleExit = async () => {
    // Opened from the companies page: just go back, keep the user signed in.
    if (fromCompanies) {
      navigate('/dashboard/companies');
      return;
    }

    try {
      signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  // Wait for whitelabel settings before choosing a flow, so main-site users never
  // briefly render the classic flow while settings are still loading.
  if (loading || !currentUser || !whitelabelLoaded) {
    return <LoadingScreen open={true} />;
  }

  // Everyone now gets the new guided, personalized onboarding flow — main
  // platform and whitelabel instances alike. The flow is whitelabel-aware
  // (hero logo, app name, support/embed domain all resolve per-instance), so
  // no gating is needed. The classic DashboardOnboarding remains as a fallback.
  const useGuidedFlow = true;

  return (
    <>
      <Button
        startIcon={<ChevronLeft sx={{ fontSize: '0.875rem !important' }} />}
        onClick={handleExit}
        sx={{
          position: 'absolute',
          bottom: 16,
          left: 16,
          zIndex: 1000,
          fontSize: '0.65rem',
          textTransform: 'none',
          minWidth: 'auto',
          padding: '4px 8px',
          backgroundColor: theme.palette.background.paper,
          '&:hover': {
            backgroundColor: theme.palette.background.paper,
          },
        }}
      >
        Exit
      </Button>

      {useGuidedFlow ? (
        <GuidedOnboarding
          currentUser={currentUser}
          getToken={getToken}
          onComplete={handleComplete}
          resumeCompanyId={resumeCompanyId}
        />
      ) : (
        <DashboardOnboarding
          currentUser={currentUser}
          getToken={getToken}
          onComplete={handleComplete}
        />
      )}
    </>
  );
}
